import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { openAIService, ChatMessage, ChatbotContext } from '../../services/openai';

interface ChatbotProps {
  /**
   * Optional: Override default minimized state
   */
  defaultMinimized?: boolean;
}

export default function Chatbot({ defaultMinimized = true }: ChatbotProps) {
  const { user } = useAuth();
  const [isMinimized, setIsMinimized] = useState(defaultMinimized);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAiAvailable, setIsAiAvailable] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Check if AI is available on mount
  useEffect(() => {
    setIsAiAvailable(openAIService.isAvailable());
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Close on Escape key
  useEffect(() => {
    if (isMinimized) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMinimized(true);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMinimized]);

  // Initialize with welcome message based on user type
  useEffect(() => {
    if (!user) return;
    
    const welcomeMessages: Record<string, string> = {
      platform_admin: "👋 Hi! I'm your Training Nudge Assistant. I can help you manage organizations, view system-wide analytics, and optimize your platform.",
      org_admin: "👋 Hi! I'm here to help you manage your team's cybersecurity training. Ask me about employee management, simulations, or analytics!",
      employee: "👋 Welcome! I'm here to support your security awareness journey. Ask me about simulations, tips for spotting phishing, or your progress!",
      individual: "👋 Hello! I'm your personal cybersecurity coach. I can help you understand threats, improve your skills, and track your learning!"
    };

    const userType = user.userType || 'individual';
    const welcomeMsg = welcomeMessages[userType] || welcomeMessages.individual;

    setMessages([
      {
        role: 'assistant',
        content: welcomeMsg,
        timestamp: new Date().toISOString()
      }
    ]);
  }, [user]);

  const buildChatContext = (): ChatbotContext | null => {
    if (!user) return null;

    return {
      userType: user.userType || 'individual',
      userId: user.id,
      orgId: user.org_id,
      userName: user.employee_data?.name || user.individual_data?.name || user.email || 'User',
    };
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || !user) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString()
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const context = buildChatContext();
      if (!context) {
        throw new Error('User context not available');
      }

      // Send to OpenAI (with fallback if not available)
      const response = await openAIService.sendChatMessage(
        messages.concat(userMessage),
        context
      );

      // Add assistant response
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      
      // Error fallback message
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment or check the Help section for guidance.",
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // If no user, don't render chatbot
  if (!user) return null;

  return (
    <>
      {/* Minimized Chatbot Button */}
      {isMinimized && (
        <button
          onClick={() => setIsMinimized(false)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center cursor-pointer group hover:scale-110"
          aria-label="Open chat assistant"
        >
          <i className="ri-message-3-line text-white text-2xl group-hover:scale-110 transition-transform"></i>
          {!isAiAvailable && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full border-2 border-white" title="AI features limited"></span>
          )}
        </button>
      )}

      {/* Expanded Chatbot Window */}
      {!isMinimized && (
        <>
        {/* Backdrop for click-outside-to-close */}
        <div className="fixed inset-0 z-40" onClick={() => setIsMinimized(true)} />
        <div className="fixed bottom-6 right-6 z-50 w-96 h-[600px] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-robot-2-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Training Nudge Assistant</h3>
                <p className="text-white/80 text-xs flex items-center gap-1">
                  {isAiAvailable ? (
                    <>
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      AI-Powered
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                      Basic Mode
                    </>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsMinimized(true)}
              className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
              aria-label="Minimize chat"
            >
              <i className="ri-subtract-line text-white text-xl"></i>
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-800">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-sm shadow-sm'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                    {message.content}
                  </p>
                  <p className={`text-xs mt-1 ${
                    message.role === 'user' ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {message.timestamp ? new Date(message.timestamp).toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    }) : ''}
                  </p>
                </div>
              </div>
            ))}

            {/* Loading Indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-gray-700 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-end space-x-2">
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask me anything..."
                disabled={isLoading}
                rows={1}
                className="flex-1 resize-none rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ minHeight: '44px', maxHeight: '120px' }}
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !inputValue.trim()}
                className="flex-shrink-0 w-11 h-11 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 cursor-pointer"
                aria-label="Send message"
              >
                <i className="ri-send-plane-fill text-lg"></i>
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              Press Enter to send • Shift+Enter for new line
            </p>
          </div>
        </div>
        </>
      )}
    </>
  );
}
